import * as React from 'react';
import { useLayoutEffect } from '../../../../hooks/use-isomorphic-layout-effect';
import {
	DEFAULT_MAX_ROWS,
	FOOTER_HEIGHT,
	HEADER_HEIGHT,
	PAGE_SIZE_BOTTOM_SPACING,
	ROW_BORDER_HEIGHT,
} from '../data-grid.constants';
import type { DataGridRowsHeight } from '../data-grid.type';
import { getRowsMinHeight } from './rows';

const DEFAULT_MIN_ROWS = 4;

/**
 * Calculates how many rows fit between the top of the grid's data rows and the bottom of the
 * viewport.
 *
 * `rowsTop` is the measured viewport offset of the first data row. Because it is measured from
 * the live layout, every fixed element above the rows (page header, breadcrumbs, tabs, action
 * bar, column header) is accounted for exactly — there is no estimated buffer.
 *
 * `rowHeight` is the fixed height every row is clamped to (the `rowsHeight` token or an explicit
 * pixel value), so dividing by it yields the exact number of rows that fit with no overflow and
 * a count that never changes between pages. Returns `null` until the grid's position has been
 * measured, so the caller can defer its first fetch instead of guessing.
 */
function calculateOptimalPageSize(rowHeight: number, rowsTop: number | null, outsidePageSize?: number): number | null {
	if (rowsTop == null) {
		return null;
	}

	const availableRowSpace = window.innerHeight - rowsTop - FOOTER_HEIGHT - PAGE_SIZE_BOTTOM_SPACING;
	const rowPitch = rowHeight + ROW_BORDER_HEIGHT;
	const rowsThatFit = Math.max(0, Math.floor(availableRowSpace / rowPitch));

	// A consumer-provided fixed page size is honored, but never beyond what the viewport can show:
	// when the requested size is larger than the rows that fit, fall back to that maximum so the
	// grid never overflows into a scroll. Without a fixed size, the grid fills the viewport up to a
	// sane default ceiling.
	const requestedRows = outsidePageSize || DEFAULT_MAX_ROWS;
	const cappedRows = Math.min(requestedRows, rowsThatFit);

	return Math.max(DEFAULT_MIN_ROWS, cappedRows);
}

/**
 * Measures where the grid's rows start in the viewport and derives how many rows fit there,
 * recalculating on window resize.
 *
 * `anchorRef` must point at the grid's table element (the column header sits at its top, so the
 * first data row starts one header height below it). The row height comes from the fixed
 * `rowsHeight` (token or pixel value) — every row is clamped to it regardless of its content, so
 * the count is known before the first fetch and stays stable across pages. Returns `null` until
 * the grid's position is measured so the caller can defer its first fetch instead of guessing.
 */
export function useOptimalPageSize(
	rowsHeight: DataGridRowsHeight | number,
	anchorRef: React.RefObject<HTMLElement | null>,
	outsidePageSize?: number
): { pageSize: number | null; remeasure: () => void } {
	const [pageSize, setPageSize] = React.useState<number | null>(null);
	const recalculateRef = React.useRef<() => void>(() => undefined);

	useLayoutEffect(() => {
		const recalculate = () => {
			const rect = anchorRef.current?.getBoundingClientRect();
			// A hidden (display: none) grid measures 0x0; keep the last real measurement so a
			// kept-alive grid does not change its page size (and refetch) every time it is shown.
			if (rect && rect.width === 0 && rect.height === 0) {
				return;
			}
			const rowsTop = rect ? rect.top + HEADER_HEIGHT : null;
			setPageSize(calculateOptimalPageSize(getRowsMinHeight(rowsHeight), rowsTop, outsidePageSize));
		};

		recalculateRef.current = recalculate;
		recalculate();

		const anchor = anchorRef.current;
		// A 0×0 table (shell still settling, or a remount before layout) is ignored above so a
		// hidden grid keeps its last size. Nothing else would measure again: the window resize
		// listener is the only other retry, which is why the grid stayed blank until the user
		// resized. Watch the table itself so the first real box starts the fetch. jsdom has no
		// ResizeObserver, so skip it there and keep the window listener.
		let observer: ResizeObserver | undefined;
		if (anchor && typeof ResizeObserver !== 'undefined') {
			observer = new ResizeObserver(() => recalculate());
			observer.observe(anchor);
		}
		window.addEventListener('resize', recalculate);

		return () => {
			observer?.disconnect();
			window.removeEventListener('resize', recalculate);
		};
	}, [rowsHeight, outsidePageSize, anchorRef]);

	const remeasure = React.useCallback(() => recalculateRef.current(), []);

	return { pageSize, remeasure };
}
